// src/pages/Public/LandingPage/v2/LandingPage.js
import React, { useMemo, useState } from "react";
import { ConfigProvider } from "antd";
import { UpOutlined } from "@ant-design/icons";

import styles from "./LandingPage.module.scss";
import { useLandingScroll } from "./v2/hooks/useLandingScroll";
import { LandingLangProvider } from "./v2/i18n/LandingLangContext";

// ✅ Barril de imágenes locales (mismas claves que IMAGES_CONFIG original)
import * as ReviImg from "../../../assets/Revi-Img";

import {
  LANDING_SECTIONS,
  LANDING_ASSET_KEYS,
  THERAPY_MODULES,
  ACADEMIC_PARTNERS,
  CLIENTS,
  PILLARS,
} from "./v2/data/landingContent";

import { Navbar } from "./v2/components/Navbar/Navbar";
import { Hero } from "./v2/components/Hero/Hero";
import { WhatIs } from "./v2/components/WhatIs/WhatIs";
import { AcademicPartners } from "./v2/components/AcademicPartners/AcademicPartners";
import { ClientsMarquee } from "./v2/components/ClientsMarquee/ClientsMarquee";
import { Therapies } from "./v2/components/Therapies/Therapies";
import { Validation } from "./v2/components/Validation/Validation";
import { Pillars } from "./v2/components/Pillars/Pillars";
import { ValueProp } from "./v2/components/ValueProp/ValueProp";
import { CtaBanner } from "./v2/components/CtaBanner/CtaBanner";
import { Footer } from "./v2/components/Footer/Footer";
import { DemoModal } from "./v2/components/DemoModal/DemoModal";
import { AwardSection } from "./v2/components/AwardSection/AwardSection";

const landingTheme = {
  token: {
    fontFamily: "'Inter', sans-serif",
    colorPrimary: "#0D47A1",
    borderRadius: 10,
  },
};

export function LandingPage() {
  const [demoOpen, setDemoOpen] = useState(false);

  const { scrolled, showScrollTop, scrollToTop, scrollToId } = useLandingScroll(
    {
      headerOffset: 84,
      scrolledThreshold: 8,
      showScrollTopThreshold: 700,
    },
  );

  // ✅ Resolver por clave (navLogo, heroImage, therapy_cognitive, logo_upo, etc.)
  const resolveAsset = useMemo(() => {
    return (key) => (key && ReviImg[key] ? ReviImg[key] : null);
  }, []);

  const navLogo = resolveAsset(LANDING_ASSET_KEYS.navLogo);
  const heroImage = resolveAsset(LANDING_ASSET_KEYS.heroImage);

  const brandIdentity = resolveAsset(LANDING_ASSET_KEYS.brandIdentity);
  const backgroundVideo = resolveAsset(LANDING_ASSET_KEYS.backgroundVideo);

  const openDemo = () => setDemoOpen(true);
  const closeDemo = () => setDemoOpen(false);

  return (
    <LandingLangProvider>
      <ConfigProvider theme={landingTheme}>
        <div className={styles.root}>
          <Navbar
            logoSrc={navLogo}
            brandAlt="REVI"
            sections={LANDING_SECTIONS}
            scrolled={scrolled}
            onScrollToId={scrollToId}
            onOpenDemo={openDemo}
          />

          <Hero heroImageSrc={heroImage} onOpenDemo={openDemo} />

          <WhatIs
            onOpenDemo={openDemo}
            videoSrc={backgroundVideo}
            logoSrc={resolveAsset("logo_big")}
            imageSrc={brandIdentity}
          />

          <AcademicPartners
            partners={ACADEMIC_PARTNERS}
            resolveLogo={resolveAsset}
          />

          <ClientsMarquee clients={CLIENTS} resolveLogo={resolveAsset} />

          <Therapies modules={THERAPY_MODULES} resolveImage={resolveAsset} />

          <Validation
            resolveImage={resolveAsset}
            creationPanelKey={LANDING_ASSET_KEYS.creationPanel}
            clinicalAnalyticsKey={LANDING_ASSET_KEYS.clinicalAnalytics}
          />

          <Pillars items={PILLARS} />
          <ValueProp />

          <AwardSection awardSrc={resolveAsset("silvereco_award")} />

          <CtaBanner onOpenDemo={openDemo} />

          <Footer
            logoSrc={navLogo}
            onScrollToId={scrollToId}
            onOpenDemo={openDemo}
          />

          <button
            type="button"
            className={`${styles.scrollTop} ${showScrollTop ? styles.scrollTopVisible : ""}`}
            onClick={scrollToTop}
            aria-label="Volver arriba"
          >
            <UpOutlined />
          </button>

          <DemoModal open={demoOpen} onClose={closeDemo} />
        </div>
      </ConfigProvider>
    </LandingLangProvider>
  );
}
